/**
 * The reach she has without anyone's API key: the platform's own records.
 *
 * This is deliberately the first place she looks and not the last. An operator
 * asking "what is holding up A2" is asking about this airfield, and answering
 * that from a search engine would be absurd — the answer is sitting in the work
 * orders. External sources are for the things genuinely outside: a regulation's
 * wording, a manufacturer's limit, a standard that changed.
 *
 * The ranking is a plain token overlap rather than anything cleverer, for a
 * reason worth writing down: an operator needs to be able to predict what a
 * search will return. A scoring function they cannot model in their head turns
 * "she didn't find it" into "it isn't there", and those are different facts.
 */
import type { DataSource } from "../api/dataSource";
import { isOpen, priorityLabel } from "../domain/workOrders";
import type { Source } from "./types";

export interface LocalFinding {
  kind: "work-order" | "alert" | "document";
  /** What to show. */
  label: string;
  /** One line of context beneath it. */
  detail: string;
  /** Where it lives, for a link. */
  href?: string;
  score: number;
}

const STOP = new Set([
  "the",
  "a",
  "an",
  "is",
  "are",
  "was",
  "were",
  "of",
  "on",
  "in",
  "at",
  "to",
  "for",
  "and",
  "or",
  "what",
  "which",
  "who",
  "how",
  "why",
  "when",
  "where",
  "do",
  "does",
  "did",
  "i",
  "me",
  "my",
  "we",
  "us",
  "it",
  "this",
  "that",
  "with",
  "about",
  "show",
]);

export function tokens(q: string): string[] {
  return q
    .toLowerCase()
    .split(/[^\p{L}\p{N}-]+/u)
    .filter((t) => t.length > 1 && !STOP.has(t));
}

/**
 * Overlap, with an exact-identifier shortcut. Someone typing "WO-000213" means
 * that record and nothing else, and burying it under six fuzzy matches because
 * they all mention "rubber" would be a worse answer than no answer.
 */
function score(haystacks: readonly (string | undefined)[], qs: readonly string[]): number {
  const hay = haystacks.filter(Boolean).join(" ").toLowerCase();
  if (!hay) return 0;
  let n = 0;
  for (const t of qs) {
    if (!hay.includes(t)) continue;
    n += /^[a-z]{2,3}-?\d{2,}$/.test(t) ? 10 : 1;
  }
  return n;
}

export interface LocalResult {
  findings: LocalFinding[];
  sources: Source[];
  /** A plain-language account of what was searched, so an empty result is readable. */
  searched: string;
}

export async function searchLocal(
  source: DataSource,
  siteId: string,
  siteCode: string,
  query: string,
  signal?: AbortSignal,
): Promise<LocalResult> {
  const qs = tokens(query);
  const searched = `work orders, alerts and the document library for ${siteCode}`;
  if (qs.length === 0) return { findings: [], sources: [], searched };

  const [wos, alerts, docs] = await Promise.all([
    source.listWorkOrders({ siteId }).catch(() => ({ items: [], truncated: false })),
    source.listAlerts(siteId).catch(() => []),
    source.listDocuments({ siteId }).catch(() => []),
  ]);
  if (signal?.aborted) return { findings: [], sources: [], searched };

  const findings: LocalFinding[] = [];

  for (const w of wos.items) {
    const sc = score([w.number, w.title, w.description, w.assetTag, w.zoneCode, w.kind, w.crew], qs);
    if (sc > 0) {
      findings.push({
        kind: "work-order",
        label: `${w.number} · ${w.title}`,
        detail: `${priorityLabel(w.priority)} · ${w.status}${w.zoneCode ? ` · ${w.zoneCode}` : ""}${w.assetTag ? ` · ${w.assetTag}` : ""}`,
        href: `/work-orders/${w.id}`,
        score: sc,
      });
    }
  }

  for (const a of alerts) {
    const sc = score([a.title, a.ruleKey, a.assetTag, a.zoneCode, a.kind], qs);
    if (sc > 0) {
      findings.push({
        kind: "alert",
        label: a.title,
        detail: `severity ${a.severity} · ${a.status}${a.zoneCode ? ` · ${a.zoneCode}` : ""}`,
        href: "/alerts",
        score: sc,
      });
    }
  }

  for (const d of docs) {
    const sc = score([d.title, d.reference, d.type, ...(d.tags ?? [])], qs);
    if (sc > 0) {
      findings.push({
        kind: "document",
        label: d.reference ? `${d.reference} · ${d.title}` : d.title,
        detail: `${d.type} · ${d.status}${d.revision ? ` · rev ${d.revision}` : ""}`,
        href: `/library/${d.id}`,
        score: sc,
      });
    }
  }

  findings.sort((a, b) => b.score - a.score);

  const sources: Source[] = [
    { kind: "local", label: `Work orders · ${siteCode}`, detail: `${wos.items.length} searched` },
    { kind: "local", label: `Alerts · ${siteCode}`, detail: `${alerts.length} searched` },
    { kind: "local", label: `Library · ${siteCode}`, detail: `${docs.length} searched` },
  ];

  return { findings: findings.slice(0, 8), sources, searched };
}

/**
 * A short factual account of where the site stands, used two ways: shown to the
 * operator when they ask what to look at, and handed to an external model as
 * context so its answer is about this airfield rather than airfields in general.
 */
export async function briefing(source: DataSource, siteId: string, siteCode: string): Promise<string> {
  const [wos, alerts] = await Promise.all([
    source.listWorkOrders({ siteId }).catch(() => ({ items: [], truncated: false })),
    source.listAlerts(siteId).catch(() => []),
  ]);
  // "Open" is the platform's own definition, not a second opinion invented here.
  const open = wos.items.filter((w) => isOpen(w.status));
  const p1 = open.filter((w) => w.priority === 1).length;
  const openAlerts = alerts.filter((a) => a.status === "open");
  const worst = openAlerts.reduce((m, a) => Math.max(m, a.severity), 0);

  return [
    `Site ${siteCode}.`,
    `${open.length} open work orders, ${p1} at P1.`,
    `${openAlerts.length} open alerts${worst ? `, highest severity ${worst}` : ""}.`,
  ].join(" ");
}

/**
 * Does the question mean "where do I start?" rather than "find me X"? Kept
 * deliberately small and literal: a wide intent classifier here would swallow
 * real searches, and the cost of missing one phrasing is a keyword search,
 * which is a survivable answer. The cost of over-matching is that "what work
 * orders mention the runway" stops searching.
 */
export function isTriageQuestion(q: string): boolean {
  const t = q.toLowerCase().trim();
  return (
    /^what should i (look at|do|start)/.test(t) ||
    /^where (do|should) i start/.test(t) ||
    /^what('s| is) (most )?(urgent|important|open)/.test(t) ||
    /^what needs (my )?attention/.test(t) ||
    t.startsWith("brief me")
  );
}

/**
 * A work-order reference in a question, or null.
 *
 * Narrow on purpose. It matches an explicit number — "WO-000012", "wo 12" —
 * and nothing looser, because the damage case is answering confidently about a
 * different order than the one asked about. A miss costs a keyword search; a
 * mismatch costs the operator's trust in every answer after it.
 */
export function workOrderRef(q: string): string | null {
  const m = /\bwo[\s-]?0*(\d{1,7})\b/i.exec(q);
  return m ? `WO-${m[1].padStart(6, "0")}` : null;
}

/** Does the question ask where something stands, rather than to find it? */
export function isStatusQuestion(q: string): boolean {
  const t = q.toLowerCase();
  return (
    /\b(status|update|where is|where's|how is|progress|state) (of |on |for )?/.test(t) ||
    /\bwhat(’s|'s| is) happening (with|on|to)\b/.test(t)
  );
}
